import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import axios from "axios";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { WishlistProvider } from "./context/WishlistContext";

import Header from "./components/Header";
import Footer from "./components/Footer";
import HomePage from "./pages/HomePage";
import ShopPage from "./pages/ShopPage";
import TrendingPage from "./pages/TrendingPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import CheckoutSuccessPage from "./pages/CheckoutSuccessPage";
import WishlistPage from "./pages/WishlistPage";
import OrdersPage from "./pages/OrdersPage";
import RefundPolicyPage from "./pages/RefundPolicyPage";
import CopyrightPolicyPage from "./pages/CopyrightPolicyPage";
import AdminPage from "./pages/AdminPage";
import AdminLoginPage from "./pages/AdminLoginPage";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import RandomPage from "./pages/RandomPage";
import ConstructionPage, { useConstructionMode } from "./pages/ConstructionPage";

import EmailSignupBanner from "./components/EmailSignupBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Remove platform badge
(function removeBadge() {
  const remove = () => {
    const el = document.getElementById('emergent-badge');
    if (el) el.parentNode.removeChild(el);
    document.querySelectorAll('a[href*="emergent-badge"], a[href*="emergent.sh"]').forEach(a => a.parentNode.removeChild(a));
  };
  remove();
  const obs = new MutationObserver(remove);
  obs.observe(document.body || document.documentElement, { childList: true, subtree: true });
})();

// Prevent right-click on images site-wide
document.addEventListener('contextmenu', (e) => {
  if (e.target.tagName === 'IMG') e.preventDefault();
});
document.addEventListener('dragstart', (e) => {
  if (e.target.tagName === 'IMG') e.preventDefault();
});

// Track user activity on page navigation
const ActivityTracker = () => {
  const location = useLocation();
  const { user } = useAuth();
  useEffect(() => {
    axios.post(`${API}/track-activity`, {
      user_id: user?.id || 'anonymous',
      action: 'page_view',
      page: location.pathname
    }).catch(() => {});
  }, [location.pathname, user?.id]);
  return null;
};

const AppLayout = ({ children }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  if (isAdminRoute) {
    return <>{children}</>;
  }

  return (
    <>
      <Header />
      <main className="min-h-screen">{children}</main>
      <Footer />
      <EmailSignupBanner />
    </>
  );
};

const AppRoutes = () => {
  const location = useLocation();
  const { isConstruction, checked } = useConstructionMode();
  const { isAdmin } = useAuth();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isLoginRoute = location.pathname === '/aaa';

  if (!checked) return null;

  // Show construction page for public routes when enabled (allow admin + login)
  if (isConstruction && !isAdminRoute && !isLoginRoute && !isAdmin) {
    return <ConstructionPage />;
  }

  return (
    <AppLayout>
      <ActivityTracker />
      <Routes>
        <Route path="/" element={<ShopPage />} />
        <Route path="/shop" element={<ShopPage />} />
        <Route path="/trending" element={<TrendingPage />} />
        <Route path="/random" element={<RandomPage />} />
        <Route path="/product/:id" element={<ProductDetailPage />} />
        <Route path="/products/:slug" element={<ProductDetailPage />} />
        <Route path="/cart" element={<CartPage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
        <Route path="/wishlist" element={<WishlistPage />} />
        <Route path="/orders" element={<OrdersPage />} />
        <Route path="/aaa" element={<AdminLoginPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/refund-policy" element={<RefundPolicyPage />} />
        <Route path="/copyright-policy" element={<CopyrightPolicyPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/admin" element={<AdminPage />} />
      </Routes>
    </AppLayout>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <WishlistProvider>
              <AppRoutes />
              <Toaster 
                position="bottom-right" 
                toastOptions={{
                  style: {
                    background: '#0A0A0A',
                    color: '#FFFFFF',
                    border: 'none',
                  },
                }}
              />
            </WishlistProvider>
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
